import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIcon, AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Typography, Theme, SxProps, Chip, Tooltip } from '@mui/material';

export type CareerPathCardProps = Readonly<{
  id?: string;
  iconName: AppIcons;
  careerPathName: string;
  careerCategoryName: string;
  onClick: () => void;
  fitLevel?: string;
  fitColor: ColorToken;
}>;

export const CareerPathCard = ({
  id,
  iconName,
  careerPathName,
  careerCategoryName,
  onClick,
  fitLevel,
  fitColor,
}: CareerPathCardProps) => {
  return (
    <Box id={id} sx={containerSx} onClick={onClick}>
      <Box sx={iconContainerSx}>
        <Box sx={iconWrapperSx(fitColor)}>
          <AppIcon iconName={iconName} diameter="20px" color="Colors/Base/White" />
        </Box>

        <AppIcon iconName="ArrowCircleUpRight" diameter="20px" color="Colors/Base/Black" />
      </Box>

      <Box sx={bottomHalfSx}>
        <Box sx={categoryAndFitSx}>
          <Typography
            variant="text-sm-medium"
            color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
          >
            {careerCategoryName}
          </Typography>

          {fitLevel && (
            <Chip
              variant="badge"
              label={fitLevel}
              size="sm"
              icon={<AppIcon iconName="Circle" diameter="8px" color={fitColor} fill={fitColor} />}
              sx={chipSx}
              type="leading"
            />
          )}
        </Box>

        <Tooltip title={careerPathName} placement="top-start">
          <Typography variant="display-xs-semibold" sx={careerPathNameSx}>
            {careerPathName}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};

const bottomHalfSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});

const categoryAndFitSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-sm'],
  alignItems: 'center',
  flexWrap: 'wrap',
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  minWidth: '216px',
  maxWidth: '100%',
  width: '100%',
  padding: backrs.spacing['spacing-2xl'],
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Gray/500']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  flex: '1 0 0',
  '&:hover': {
    boxShadow: backrs.shadows['shadow-sm'],
    transform: 'translate(-2px, -2px)',
  },
});

const iconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-xs'],
  justifyContent: 'space-between',
});

const iconWrapperSx =
  (fitColor: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    padding: '10px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: backrs.radius['radius-md'],
    border: `2px solid ${backrs.colors['Beige/950']}`,
    backgroundColor: backrs.colors[fitColor],
  });

const chipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  '& .MuiChip-icon': {
    marginLeft: backrs.spacing['spacing-none'],
    marginRight: backrs.spacing['spacing-none'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .MuiChip-label': {
    ...typography['text-sm-medium'],
  },
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  boxShadow: backrs.shadows['shadow-xs'],
});

const careerPathNameSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-xs'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
  minWidth: 0,
});
